import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Clock, AlertTriangle, Tag, MessageSquare,
  ThumbsUp, ThumbsDown, Quote, User, PhoneIncoming, PhoneOutgoing,
  PhoneCall, Users, ArrowRight, CheckCircle, XCircle, Mic, Hash
} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './CallDetail.css';

const formatDuration = (ms) => {
  if (!ms && ms !== 0) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const formatClock = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const sentimentClass = (s) => {
  if (s === 'positive') return 'positive';
  if (s === 'negative') return 'negative';
  return 'neutral';
};

// "SALEH ALOJAIRI(1205)" -> { name: "SALEH ALOJAIRI", ext: "1205" }
// Raw phone number -> { name: null, ext: null }
const parseParty = (s) => {
  if (!s) return { raw: '—', name: null, ext: null, isAgent: false };
  const m = String(s).match(/^(.+?)\((\d+)\)\s*$/);
  if (m) return { raw: s, name: m[1].trim(), ext: m[2], isAgent: true };
  return { raw: s, name: null, ext: null, isAgent: false };
};

// Decide who the "agent on this segment" is, given the call direction and
// the raw per-recording party strings.
const pickAgentOnSegment = (seg) => {
  const receiver = parseParty(seg.receiving_party);
  const caller   = parseParty(seg.calling_party);
  if (seg.direction === 'outbound' && caller.isAgent)   return caller;
  if (seg.direction === 'inbound'  && receiver.isAgent) return receiver;
  // internal / unknown — pick whichever looks like an agent, preferring receiver
  if (receiver.isAgent) return receiver;
  if (caller.isAgent)   return caller;
  return receiver; // fall back to receiver even if not in agent format
};

const DirectionIcon = ({ direction, size = 16 }) => {
  if (direction === 'inbound')  return <PhoneIncoming size={size} />;
  if (direction === 'outbound') return <PhoneOutgoing size={size} />;
  return <PhoneCall size={size} />;
};

const CallDetail = () => {
  const { id } = useParams();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showError } = useToast();

  useEffect(() => {
    const fetchCall = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/calls/${id}`);
        if (res.data.success) setCall(res.data.call);
      } catch (err) {
        console.error(err);
        showError('Failed to load call');
      } finally {
        setLoading(false);
      }
    };
    fetchCall();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="call-detail-loading">
        <div className="spinner" />
        <p>Loading call...</p>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="call-detail-page">
        <Link to="/calls" className="back-link"><ArrowLeft size={16} /> Back to calls</Link>
        <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '2rem' }}>Call not found.</p>
      </div>
    );
  }

  const a = call.analysis;
  const segments = Array.isArray(call.segments) ? call.segments : [];
  const hasTransfer = segments.length > 1;

  // Who was on THIS recording (for the top meta line)
  const thisSegment = segments.find(s => s.id === call.id) || call;
  const thisAgent = pickAgentOnSegment(thisSegment);

  return (
    <div className="call-detail-page">
      <Link to="/calls" className="back-link"><ArrowLeft size={16} /> Back to calls</Link>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="call-header">
          <h1>
            <DirectionIcon direction={call.direction} size={28} />
            {call.direction
              ? `${call.direction.charAt(0).toUpperCase()}${call.direction.slice(1)} call`
              : 'Call'}
            {call.answered === false && (
              <span className="badge neutral" style={{ marginLeft: '0.75rem', fontSize: '0.75rem' }}>
                <XCircle size={12} /> Not answered
              </span>
            )}
            {hasTransfer && (
              <span className="badge" style={{
                marginLeft: '0.75rem', fontSize: '0.75rem',
                background: 'rgba(139, 92, 246, 0.2)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)'
              }}>
                <Users size={12} /> Transferred ({segments.length} segments)
              </span>
            )}
          </h1>
          <div className="call-meta">
            <span><Clock size={14} /> {formatTime(call.start_time)}</span>
            <span>Call length: {formatDuration(call.duration_ms)}</span>
            {call.talking_duration_ms != null && (
              <span><Mic size={14} /> Talking: {formatDuration(call.talking_duration_ms)}</span>
            )}
            {call.caller_number && <span>Customer: {call.caller_number}</span>}
            {thisAgent.isAgent && (
              <span>
                <User size={14} /> {thisAgent.name} (ext {thisAgent.ext})
              </span>
            )}
            {call.dialed_number && <span>Dialed: {call.dialed_number}</span>}
            {call.initial_group && <span>Group: {call.initial_group}</span>}
          </div>
        </div>

        {/* Audio player */}
        {call.audio_url ? (
          <div className="audio-card">
            <audio controls src={call.audio_url} style={{ width: '100%' }} />
          </div>
        ) : (
          <div className="audio-card" style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
            No audio available for this recording.
          </div>
        )}

        {/* Transfer timeline — only if there's more than one segment */}
        {hasTransfer && (
          <div className="card">
            <h3><Users size={18} /> Call journey ({segments.length} segments)</h3>
            <div className="transfer-chain">
              {segments.map((seg, i) => {
                const segAgent = pickAgentOnSegment(seg);
                const isCurrent = seg.id === call.id;
                return (
                  <React.Fragment key={seg.id}>
                    <div className={`transfer-step ${isCurrent ? 'current' : ''}`}>
                      <div className="transfer-step-head">
                        <span className="transfer-step-num">Seg {i + 1}</span>
                        <span className="transfer-step-time">{formatClock(seg.start_time)}</span>
                      </div>
                      <div className="transfer-step-agent">
                        <User size={14} />
                        {segAgent.isAgent
                          ? <strong>{segAgent.name}</strong>
                          : <span style={{ color: 'rgba(255,255,255,0.6)' }}>{segAgent.raw}</span>}
                        {segAgent.ext && <span className="transfer-step-ext">ext {segAgent.ext}</span>}
                      </div>
                      <div className="transfer-step-meta">
                        <span>{formatDuration(seg.duration_ms)}</span>
                        {seg.talking_duration_ms != null && (
                          <span>· {formatDuration(seg.talking_duration_ms)} talking</span>
                        )}
                      </div>
                      {!isCurrent && seg.id && (
                        <Link to={`/calls/${seg.id}`} className="transfer-step-link">
                          Open recording →
                        </Link>
                      )}
                      {isCurrent && (
                        <span className="transfer-step-current-pill">You are here</span>
                      )}
                    </div>
                    {i < segments.length - 1 && (
                      <ArrowRight size={20} className="transfer-arrow" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Structured info grid — everything we know about this call */}
        <div className="card">
          <h3><Hash size={18} /> Call info</h3>
          <div className="info-grid">
            <InfoRow label="Direction" value={call.direction && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'capitalize' }}>
                <DirectionIcon direction={call.direction} size={14} /> {call.direction}
              </span>
            )} />
            <InfoRow label="Answered" value={
              call.answered === true
                ? <span style={{ color: '#10b981', display:'inline-flex', alignItems:'center', gap: 4 }}><CheckCircle size={14}/> Yes</span>
                : call.answered === false
                  ? <span style={{ color: '#ef4444', display:'inline-flex', alignItems:'center', gap: 4 }}><XCircle size={14}/> No</span>
                  : '—'
            } />
            <InfoRow label="Started"   value={formatTime(call.start_time)} />
            <InfoRow label="Ended"     value={formatTime(call.end_time)} />
            <InfoRow label="Customer phone" value={call.caller_number || '—'} />
            <InfoRow label="Dialed number"  value={call.dialed_number || '—'} />
            <InfoRow label="Primary agent"  value={call.agent_name
              ? `${call.agent_name}${call.agent_extension ? ` (ext ${call.agent_extension})` : ''}`
              : '—'} />
            <InfoRow label="Initial group" value={call.initial_group || '—'} />
            <InfoRow label="Call length"   value={formatDuration(call.duration_ms)} />
            <InfoRow label="Talking time"  value={call.talking_duration_ms != null
              ? formatDuration(call.talking_duration_ms)
              : '—'} />
            <InfoRow label="Chronicall call ID" value={call.chronicall_call_id || '—'} mono />
            <InfoRow label="Status"        value={<span className="badge-tag">{call.status || '—'}</span>} />
          </div>
        </div>

        {/* KPIs from analysis */}
        {a && (
          <div className="detail-kpis">
            <div className="detail-kpi-card">
              <div className="detail-kpi-label">Sentiment</div>
              <div className="detail-kpi-value">
                <span className={`badge ${sentimentClass(a.sentiment)}`}>{a.sentiment}</span>
              </div>
            </div>
            <div className="detail-kpi-card">
              <div className="detail-kpi-label">Emotion</div>
              <div className="detail-kpi-value">{a.detected_emotion || '—'}</div>
            </div>
            <div className="detail-kpi-card">
              <div className="detail-kpi-label">Category</div>
              <div className="detail-kpi-value">
                <span className="badge-tag"><Tag size={12} /> {a.issue_category || '—'}</span>
              </div>
            </div>
            <div className="detail-kpi-card">
              <div className="detail-kpi-label">Urgency</div>
              <div className="detail-kpi-value">
                {a.urgency_flag ? (
                  <span className="badge negative"><AlertTriangle size={12} /> Urgent</span>
                ) : '—'}
              </div>
            </div>
          </div>
        )}

        {/* Summary bullets */}
        {a?.summary_bullets?.length > 0 && (
          <div className="card">
            <h3><MessageSquare size={18} /> Customer issues summary</h3>
            <ul className="bullets">
              {a.summary_bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        )}

        {/* Pros / Cons */}
        {(a?.pros?.length > 0 || a?.cons?.length > 0) && (
          <div className="pros-cons-grid">
            {a.pros?.length > 0 && (
              <div className="card">
                <h3><ThumbsUp size={18} color="#10b981" /> Positive</h3>
                <ul className="bullets">{a.pros.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </div>
            )}
            {a.cons?.length > 0 && (
              <div className="card">
                <h3><ThumbsDown size={18} color="#ef4444" /> Negative</h3>
                <ul className="bullets">{a.cons.map((c, i) => <li key={i}>{c}</li>)}</ul>
              </div>
            )}
          </div>
        )}

        {/* Topics & key phrases */}
        {(a?.topics?.length > 0 || a?.key_phrases?.length > 0) && (
          <div className="card">
            {a.topics?.length > 0 && (
              <>
                <h4><Tag size={14} /> Topics</h4>
                <div className="pill-row">
                  {a.topics.map((t, i) => <span key={i} className="pill">{t}</span>)}
                </div>
              </>
            )}
            {a.key_phrases?.length > 0 && (
              <>
                <h4 style={{ marginTop: '1rem' }}><Quote size={14} /> Key phrases</h4>
                <div className="pill-row">
                  {a.key_phrases.map((p, i) => <span key={i} className="pill quote">"{p}"</span>)}
                </div>
              </>
            )}
          </div>
        )}

        {/* Transcript */}
        <div className="card">
          <h3>Transcript {call.transcript_language && <span className="lang-tag">{call.transcript_language}</span>}</h3>
          <pre className="transcript">{call.transcript_text || '(no transcript available)'}</pre>
        </div>

        {/* Error block (if this call failed in the pipeline) */}
        {call.error && (
          <div className="card" style={{ borderColor: 'rgba(239,68,68,0.4)' }}>
            <h3 style={{ color: '#ef4444' }}><AlertTriangle size={18} /> Pipeline error</h3>
            <pre className="transcript" style={{ color: '#fecaca' }}>{call.error}</pre>
            {call.retry_count != null && (
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                Retry count: {call.retry_count}
              </p>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

const InfoRow = ({ label, value, mono }) => (
  <div className="info-row">
    <span className="info-label">{label}</span>
    <span className={`info-value ${mono ? 'mono' : ''}`}>{value ?? '—'}</span>
  </div>
);

export default CallDetail;
